/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { forwardRef, useEffect, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import { cssMap, cx } from '@atlaskit/css';
import { easeOut } from '@atlaskit/motion';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useSourceAnalyticActions } from '@atlassian/ai-smart-button-api';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { RovoSkillsConfigProvider } from '@atlassian/rovo-skills/services/rovo-skills-config-provider';

import { useRovoButtonEventsInstrumentation } from '../../hooks/useRovoButtonEventsInstrumentation';
import { AIMiniModal, preloadAIMiniModal } from '../../ui/ai-mini-modal';
import { useDebugNudgeTriggers } from '../common/hooks/use-debug-nudge-triggers';
import type { AISmartButtonProps } from '../common/types/shared-types';
import { isBrowser } from '../common/utils/is-browser';

import { AISmartButtonUI } from './components/ai-smart-button-ui';
import { selectionCountsInMainEditor } from './utils/pmSelectionCounts';

const ANIMATION_DURATION = 150;

const styles = cssMap({
	wrapper: { position: 'relative' },
	animatedMiniModalBefore: {
		transformOrigin: 'right bottom',
		visibility: 'hidden',
		opacity: token('opacity.disabled'),
		transition: `transform ${ANIMATION_DURATION}ms ${easeOut}, opacity ${ANIMATION_DURATION}ms ${easeOut}, visibility ${ANIMATION_DURATION}ms ${easeOut}, filter ${ANIMATION_DURATION}ms ${easeOut}`,
		transform: 'scale(0)',
		filter: 'blur(8px)',
		willChange: 'transform, opacity, visibility, filter',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'all 1ms',
		},
	},
	animatedMiniModalAfter: {
		opacity: 1,
		transform: 'scale(1)',
		visibility: 'visible',
		filter: 'blur(0px)',
	},
});

export const AISmartButton = forwardRef<HTMLButtonElement, AISmartButtonProps>(
	(
		{
			product,
			sidebarApi,
			productPage,
			analyticsAttributes,
			isDisabled = false,
			getActions,
			tags,
			// TAKE CARE when destructuring more props from `delegatedProps` as
			// these are passed into `AIMiniModal` below. If you must destructure an attribute from here,
			// make sure to explicitly pass the attribute into `AIMiniModal`
			...delegatedProps
		},
		forwardedRef,
	) => {
		const hoverTimerRef = React.useRef<NodeJS.Timeout | null>(null);
		const { fireRovoButtonUIEvent } = useRovoButtonEventsInstrumentation();
		const { setButtonClickedTimestamp } = useSourceAnalyticActions();
		const { mode, toggleConversationAssistantUIMode, setIsMiniModalAvailable } =
			useRovoExperienceAPIStore();

		const [rovoButtonCanRender, setRovoButtonCanRender] = useState(mode !== 'mini-modal');
		const [hasStartedPreloading, setHasStartedPreloading] = useState(false);

		useDebugNudgeTriggers();

		useEffect(() => {
			setIsMiniModalAvailable(true);

			if (!isBrowser()) {
				return;
			}

			// Start preloading when mouse is within the bottom-right box (300px from bottom, 300px from right)
			const handleMouseMove = (event: MouseEvent) => {
				if (isBrowser() && !hasStartedPreloading) {
					const viewportHeight = window.innerHeight;
					const viewportWidth = window.innerWidth;
					const mouseY = event.clientY;
					const mouseX = event.clientX;

					const distanceFromBottom = viewportHeight - mouseY;
					const distanceFromRight = viewportWidth - mouseX;

					if (distanceFromBottom <= 300 && distanceFromRight <= 300) {
						preloadAIMiniModal();
						setHasStartedPreloading(true);
					}
				}
			};

			const unbind = bindAll(window, [
				{
					type: 'mousemove',
					listener: handleMouseMove,
				},
			]);

			return () => {
				setIsMiniModalAvailable(false);
				isBrowser() && unbind();
			};
		}, [setIsMiniModalAvailable, hasStartedPreloading]);

		useEffect(() => {
			fireRovoButtonUIEvent({
				source: productPage,
				action: 'rendered',
				actionSubject: 'rovoButton',
				actionSubjectId: 'aiSmartButton',
				attributes: {
					product,
					productPage,
					...analyticsAttributes,
					version: 'refresh',
					legacy: false,
				},
				product,
			});
		}, [fireRovoButtonUIEvent, product, productPage, analyticsAttributes]);

		useEffect(() => {
			// Wait for the closing animation of modal to complete before showing the rovo button
			if (mode !== 'mini-modal') {
				setTimeout(() => {
					setRovoButtonCanRender(true);
				}, ANIMATION_DURATION);
				return;
			}

			setRovoButtonCanRender(false);
		}, [mode, setRovoButtonCanRender]);

		const onAISmartButtonClick = React.useCallback(
			(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
				// Set timestamp immediately on click
				setButtonClickedTimestamp(Date.now());

				toggleConversationAssistantUIMode({
					targetMode: 'mini-modal',
					sidebarApi,
				});

				const selectionAttributes: { totalSelectedChars?: number; totalSelectedWords?: number } =
					{};
				if (fg('platform_editor_ai_aifc_streaming')) {
					const counts = selectionCountsInMainEditor(e.currentTarget);
					if (counts != null) {
						selectionAttributes.totalSelectedWords = counts.words;
						selectionAttributes.totalSelectedChars = counts.chars;
					}
				}

				fireRovoButtonUIEvent({
					source: productPage,
					action: 'clicked',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiRovoSmartButton',
					attributes: {
						product,
						productPage,
						...analyticsAttributes,
						...selectionAttributes,
						version: 'refresh',
					},
					product,
				});

				setRovoButtonCanRender(false);
			},
			[
				product,
				productPage,
				analyticsAttributes,
				fireRovoButtonUIEvent,
				toggleConversationAssistantUIMode,
				sidebarApi,
				setButtonClickedTimestamp,
			],
		);

		const onAISmartButtonEnter = React.useCallback(() => {
			hoverTimerRef.current = setTimeout(() => {
				fireRovoButtonUIEvent({
					source: productPage,
					action: 'hovered',
					actionSubject: 'rovoButton',
					actionSubjectId: 'aiSmartButton',
					attributes: {
						product,
						productPage,
						...analyticsAttributes,
						version: 'refresh',
					},
					product,
				});
			}, 1000);
		}, [fireRovoButtonUIEvent, product, productPage, analyticsAttributes]);

		const onAISmartButtonLeave = React.useCallback(() => {
			if (hoverTimerRef.current) {
				clearTimeout(hoverTimerRef.current);
				hoverTimerRef.current = null;
			}
		}, []);

		return (
			<RovoSkillsConfigProvider config={{ tags }}>
				<Box xcss={styles.wrapper}>
					<Box
						xcss={cx(
							styles.animatedMiniModalBefore,
							mode === 'mini-modal' && styles.animatedMiniModalAfter,
						)}
					>
						{!sidebarApi.isSidebarOpen() && !rovoButtonCanRender && (
							<AIMiniModal
								product={product}
								sidebarApi={sidebarApi}
								getActions={getActions}
								{...delegatedProps}
							/>
						)}
					</Box>
					{!sidebarApi.isSidebarOpen() && rovoButtonCanRender && (
						<AISmartButtonUI
							ref={forwardedRef}
							onClick={onAISmartButtonClick}
							isDisabled={isDisabled}
							onMouseEnter={onAISmartButtonEnter}
							onMouseLeave={onAISmartButtonLeave}
							sidebarApi={sidebarApi}
							product={product}
							productPage={productPage}
							analyticsAttributes={analyticsAttributes}
							getActions={getActions}
						/>
					)}
				</Box>
			</RovoSkillsConfigProvider>
		);
	},
);
